import { useState, useEffect, useMemo, useCallback } from 'react'
import ParseAPI from '@api/ParseAPI'
import cn from 'clsx'
import {
    RECORD_TYPE_CHECK,
    RECORD_TYPE_DOCUMENT,
    CHECK_NOT_REVIEWED_STATUSES,
    CHECK_REVIEW_IN_PROGRESS_STATUSES,
    CHECK_REVIEWED_STATUSES,
    DOC_NOT_REVIEWED_STATUSES,
    DOC_REVIEW_IN_PROGRESS_STATUSES,
    DOC_REVIEWED_STATUSES,
} from '@constants'
import { createQueryString, debounce } from '@utils'

import PartnersRecordsTable from './PartnersRecordsTable'
import Pagination from '@components/shared/Pagination'

import './Partners.css'
import { Link } from 'react-router-dom'

const tabs = [
    'IN REVIEW',
    'NEEDS REVIEW',
    'REVIEW COMPLETE',
]

const recordsPerPage = 20

const baseUrls = {
    [RECORD_TYPE_CHECK]: '/checks',
    [RECORD_TYPE_DOCUMENT]: '/document',
}

const NOT_REVIEWED_STATUSES = {
    [RECORD_TYPE_CHECK]: CHECK_NOT_REVIEWED_STATUSES,
    [RECORD_TYPE_DOCUMENT]: DOC_NOT_REVIEWED_STATUSES,
}

const REVIEW_IN_PROGRESS_STATUSES = {
    [RECORD_TYPE_CHECK]: CHECK_REVIEW_IN_PROGRESS_STATUSES,
    [RECORD_TYPE_DOCUMENT]: DOC_REVIEW_IN_PROGRESS_STATUSES,
}

const REVIEWED_STATUSES = {
    [RECORD_TYPE_CHECK]: CHECK_REVIEWED_STATUSES,
    [RECORD_TYPE_DOCUMENT]: DOC_REVIEWED_STATUSES,
}

const getFetchChecksParams = (statuses, orderField, orderDirection, pageIndex, debouncedQuery) => [
    statuses.map(it => ['statuses[]', it]),
    {
        orderField,
        orderDirection,
        offset: Math.round(pageIndex * recordsPerPage),
        limit: recordsPerPage,
        queryField: 'name',
        queryValue: debouncedQuery,
        withCount: true,
    },
]

const getFetchDocumentsParams = (statuses, orderField, orderDirection, pageIndex, debouncedQuery) => [
    statuses.map(it => ['orgStatuses[]', it]),
    {
        orderField,
        orderDirection,
        offset: Math.round(pageIndex * recordsPerPage),
        limit: recordsPerPage,
        queryField: 'name',
        queryValue: debouncedQuery,
    },
]

const PartnersRecordsBase = ({
    title,
    recordType,
}) => {
    
    const [currentTab, setCurrentTab] = useState(1)
    
    const [notReviewed, setNotReviewed] = useState([])
    const [notReviewedCount, setNotReviewedCount] = useState(0)
    
    const [inReview, setInReview] = useState([])
    const [inReviewCount, setInReviewCount] = useState(0)
    
    const [reviewed, setReviewed] = useState([])
    const [reviewedCount, setReviewedCount] = useState(0)
    
    const [orderField, setOrderField] = useState('updatedAt')
    const [orderDirection, setOrderDirection] = useState('desc')
    
    const [query, setQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    
    const baseUrl = useMemo(() => baseUrls[recordType], [recordType])
    
    const getFetchRecordsParams = useMemo(() => {
        switch (recordType) {
            case RECORD_TYPE_CHECK: return getFetchChecksParams
            case RECORD_TYPE_DOCUMENT: return getFetchDocumentsParams
            default: return null
        }
    }, [recordType])
    
    const debouncedSetQuery = useMemo(
        () => debounce(setDebouncedQuery, 500),
        [],
    )
    
    const updateQueryDebounced = useCallback(
        value => debouncedSetQuery(value),
        [debouncedSetQuery],
    )
    
    const updateQuery = useCallback((value, setImmediate = false) => {
        
        setQuery(value)
        
        if (setImmediate)
            setDebouncedQuery(value)
        else
            updateQueryDebounced(value)
        
    }, [updateQueryDebounced])
    
    const fetchRecordGroup = useCallback(async (statuses, pageIndex) => {
        
        if (!debouncedQuery?.length)
            console.warn('fetchRecordGroup NO QUERY')
        else
            console.warn('fetchRecordGroup QUERY', debouncedQuery)
        
        const params = createQueryString(...getFetchRecordsParams(
            statuses,
            orderField,
            orderDirection,
            pageIndex,
            debouncedQuery,
        ))
        
        try {
            return ParseAPI.get(`${baseUrl}${params}`)
        } catch (e) {
            // @todo show error
            console.error('fetchRecordGroup', e)
            return null
        }
        
    }, [orderField, orderDirection, debouncedQuery])
    
    const fetchInReviewRecords = useCallback(async pageIndex => {
        
        const res = await fetchRecordGroup(REVIEW_IN_PROGRESS_STATUSES[recordType], pageIndex)
        
        setInReview(res?.data?.rows ?? [])
        setInReviewCount(res?.data?.count ?? 0)
        
    }, [query, debouncedQuery])
    
    const fetchNeedsReviewRecords = useCallback(async pageIndex => {
        
        const res = await fetchRecordGroup(NOT_REVIEWED_STATUSES[recordType], pageIndex)
        
        setNotReviewed(res?.data?.rows ?? [])
        setNotReviewedCount(res?.data?.count ?? 0)
        
    }, [query, debouncedQuery])
    
    const fetchReviewedRecords = useCallback(async pageIndex => {
        
        const res = await fetchRecordGroup(REVIEWED_STATUSES[recordType], pageIndex)
        
        setReviewed(res?.data?.rows ?? [])
        setReviewedCount(res?.data?.count ?? 0)
        
    }, [query, debouncedQuery])
    
    const fetchRecordGroups = useCallback(async () => Promise.all([
        fetchNeedsReviewRecords(0),
        fetchInReviewRecords(0),
        fetchReviewedRecords(0),
    ]), [query, debouncedQuery])
    
    const onInReviewPageClick = e => fetchNeedsReviewRecords(e.selected)
    const onNotReviewedPageClick = e => fetchNeedsReviewRecords(e.selected)
    const onReviewedPageClick = e => fetchNeedsReviewRecords(e.selected)
    
    const updateOrderField = useCallback(key => {
        
        if (key === orderField) {
            setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setOrderField(key)
            setOrderDirection('desc')
        }
        
        if (currentTab === 0) fetchInReviewRecords()
        else if (currentTab === 1) fetchNeedsReviewRecords()
        else if (currentTab === 2) fetchReviewedRecords()
        
    }, [currentTab, orderField, orderDirection])
    
    useEffect(() => { fetchRecordGroups() }, [debouncedQuery])
    
    const createPagination = useCallback((itemCount, onPageChange) => {
        
        if (itemCount < 1 || query?.length)
            return null
        
        return (
            <Pagination
                className="justify-center"
                count={itemCount}
                itemsPerPage={recordsPerPage}
                onPageChange={onPageChange} />
        )
        
    }, [query])
    
    const tabData = [{
        count: inReviewCount,
        rows: inReview,
        action: onInReviewPageClick,
    }, {
        count: notReviewedCount,
        rows: notReviewed,
        action: onNotReviewedPageClick,
    }, {
        count: reviewedCount,
        rows: reviewed,
        action: onReviewedPageClick,
    }]
    
    const currentTabData = tabData[currentTab]
    
    return (
        
        <div className="PartnersRecordsBase">
            
            <div className="flex items-center content-center justify-between w-full">
                
                <div className="text-sm breadcrumbs">
                    <ul>
                        <li><Link to="/partners">Partner Annotations Portal</Link></li> 
                        <li>{title}</li>
                    </ul>
                </div>
                
                <div className="flex items-center content-center justify-center text-center tabs tabs-boxed">
                    {tabs.map((it, i) => (
                        <button
                            key={it}
                            className={cn('nav-item tab', { 'tab-active': i === currentTab })}
                            onClick={() => setCurrentTab(i)}>
                            {it}
                        </button>
                    ))}
                </div>
                
                <div className="">
                    <input
                        type="text"
                        autoComplete="off"
                        className="input input-bordered"
                        value={query}
                        placeholder="Search..."
                        onKeyUp={e => e.key === 'Escape' && updateQuery('', true)}
                        onChange={e => updateQuery(e.target.value?.toLowerCase() ?? '')} />
                </div>
            
            </div>
            
            <div className="mt-4">
                
                <div className="flex flex-col items-center gap-2">
                    {createPagination(currentTabData.count, onInReviewPageClick)}
                    <PartnersRecordsTable
                        recordType={recordType}
                        records={currentTabData.rows}
                        orderField={orderField}
                        orderDirection={orderDirection}
                        query={query}
                        onHeaderClick={updateOrderField} />
                </div>
            
            </div>
        
        </div>
        
    )
    
}

export default PartnersRecordsBase
